import React, { useState, useEffect } from 'react';
import Button from '../../components/shared/custom-btn';
import CustomInput from '../../components/shared/custom-input';
import CustomTextarea from '../../components/shared/custom-textarea';
import CustomReactSelect from '../../components/shared/custom-react-selector';
import { RxCross2 } from 'react-icons/rx';
import { Country, State, City } from 'country-state-city';
import ModalHeading from '../../components/shared/modal-heading';
import { createVendors } from '../../services/vendors-service';
import GoogleMapComponent from '../../components/shared/google-map';


const AddModal = ({ handleCloseAddModal, onAdd, accountId, openEditModal }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    address: '',
    country: '',
    state: '',
    city: '',
    postalCode: '',
    location: '51.505,-0.09',
    note: '',
  });

  const [showMap, setShowMap] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Category options
  const categoryOptions = [
    { value: '', label: 'Select Category' },
    { value: 'Retail', label: 'Retail' },
    { value: 'Service', label: 'Service' },
    { value: 'Manufacturing', label: 'Manufacturing' },
  ];

  // Load countries on component mount
  useEffect(() => {
    const countries = Country.getAllCountries().map((country) => ({
      value: country.name, // Store country name instead of isoCode
      label: country.name,
      isoCode: country.isoCode
    }));
    setCountryOptions([{ value: '', label: 'Select Country' }, ...countries]);
  }, []);


  // Update states when country changes
  useEffect(() => {
    if (formData.country) {
      const selectedCountry = countryOptions.find(c => c.value === formData.country);
      if (selectedCountry) {
        const states = State.getStatesOfCountry(selectedCountry.isoCode).map((state) => ({
          value: state.name, // Store state name instead of isoCode
          label: state.name,
          isoCode: state.isoCode // Keep isoCode for city lookup
        }));
        setStateOptions([{ value: '', label: 'Select State' }, ...states]);
        setFormData((prev) => ({ ...prev, state: '', city: '' }));
        setCityOptions([{ value: '', label: 'Select City' }]);
      }
    } else {
      setStateOptions([{ value: '', label: 'Select State' }]);
      setCityOptions([{ value: '', label: 'Select City' }]);
    }
  }, [formData.country]);

  // Update cities when state changes
  useEffect(() => {
    if (formData.country && formData.state) {
      const selectedCountry = countryOptions.find(c => c.value === formData.country);
      const selectedState = stateOptions.find(s => s.value === formData.state);

      if (selectedCountry && selectedState) {
        const cities = City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode).map((city) => ({
          value: city.name,
          label: city.name,
        }));
        setCityOptions([{ value: '', label: 'Select City' }, ...cities]);
        setFormData((prev) => ({ ...prev, city: '' }));
      }
    } else {
      setCityOptions([{ value: '', label: 'Select City' }]);
    }
  }, [formData.country, formData.state]);


  // Handle input changes for text fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle select changes for dropdowns
  const handleSelectChange = (name, option) => {
    setFormData((prev) => ({
      ...prev,
      [name]: option ? option.value : '',
      // Reset dependent fields when country or state changes
      ...(name === 'country' && { state: '', city: '' }),
      ...(name === 'state' && { city: '' })
    }));
  };

  const initialLocation = formData.location.split(',').map(Number);
  const mapPosition = { lat: initialLocation[0], lng: initialLocation[1] };

  // Handle form submission
  const handleSave = async (e) => {
    e.preventDefault();
    if (isLoading || successMessage) return;
    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      console.log(formData)
      const response = await createVendors(formData, accountId);
      console.log(response);
      if (response.success) {
        const newItem = response.data.data;
        onAdd?.(newItem);
        openEditModal?.(newItem);
        setSuccessMessage('Vendor added successfully!');
        setTimeout(() => {
          handleCloseAddModal();
        }, 1000);
      } else {
        setErrorMessage(response.data.message || 'Failed to add vendor');
      }
    } catch (error) {
      console.error('Error adding vendor:', error);
      setErrorMessage('An error occurred while adding the vendor');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLocationChange = (location) => {
    setFormData((prev) => ({ ...prev, location }));
  };

  return (
    <form onSubmit={handleSave} className="space-y-2 pt-3 relative">
      <ModalHeading className="text-left mb-3 border-b border-gray-300 px-4 pb-2"> Add Vendor Details</ModalHeading>

      <button
        className="hover:scale-105 absolute -top-2 right-2 bg-white border p-2 rounded-full"
        onClick={handleCloseAddModal}
      >
        <RxCross2 className="text-xl" />
      </button>

      {successMessage && (
        <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-md text-sm text-center">
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="mb-4 px-4 py-2 bg-red-100 text-red-800 rounded-md text-sm text-center">
          {errorMessage}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 px-4 gap-y-2 gap-x-2">
        <CustomInput
          label="Name"
          name="name"
          type="text"
          value={formData.name}
          onChange={handleChange}
          inputStyle="!py-[3px]"
          labelGap={'!gap-0.5'}
          labelStyle={'text-gray-700'}
          required={true}
          disabled={isLoading}
        />

        <CustomReactSelect
          label="Category"
          name="category"
          value={categoryOptions.find((option) => option.value === formData.category) || null}
          onChange={(option) => handleSelectChange('category', option)}
          options={categoryOptions}
          placeholder='Select category'
          required={true}
          isDisabled={isLoading}
        />
        <CustomInput
          label="Address"
          name="address"
          type="text"
          value={formData.address}
          onChange={handleChange}
          inputStyle="!py-[3px]"
          labelGap={'!gap-0.5'}
          labelStyle={'text-gray-700'}
          // required={true}
          isDisabled={isLoading}
        />

        <CustomReactSelect
          label="Country"
          name="country"
          value={countryOptions.find((option) => option.value === formData.country) || null}
          onChange={(option) => handleSelectChange('country', option)}
          options={countryOptions}
          placeholder='Select country'
          // required={true}
          isDisabled={isLoading}
        />

        <CustomReactSelect
          label="State"
          name="state"
          value={stateOptions.find((option) => option.value === formData.state) || null}
          onChange={(option) => handleSelectChange('state', option)}
          options={stateOptions}
          // required={true}
          isDisabled={!formData.country || isLoading}
        />

        <CustomReactSelect
          label="City"
          name="city"
          value={cityOptions.find((option) => option.value === formData.city) || null}
          onChange={(option) => handleSelectChange('city', option)}
          options={cityOptions}
          // required={true}
          isDisabled={!formData.state || isLoading}
        />

        <CustomInput
          label="Postal Code"
          name="postalCode"
          type="text"
          value={formData.postalCode}
          onChange={handleChange}
          inputStyle="!py-[3px]"
          labelGap={'!gap-0.5'}
          labelStyle={'text-gray-700'}
          // required={true}
          maxLength={10}
          disabled={isLoading}
        />

        <div className={`flex flex-col sm:col-span-2 ${showMap ? 'mb-20' : 'mb-0'} gap-1.5 w-full`}>
          <button
            type="button"
            className={` bg-gray-200 px-2 text-sm py-1.5 rounded-lg mt-2`}
            onClick={() => setShowMap(!showMap)}
          >
            {showMap ? 'Hide Map' : 'Select Location on Map'}
          </button>
          <GoogleMapComponent
            isVisible={showMap}
            initialLocation={mapPosition}
            onLocationChange={handleLocationChange}
          />
        </div>

        <div className="w-full sm:col-span-2">
          <CustomTextarea
            label="Note"
            name="note"
            value={formData.note}
            onChange={handleChange}
            placeholder=""
            labelGap={'!gap-0.5'}
            labelStyle={'text-gray-700'}
            rows={2}
            maxLength={500}
            disabled={isLoading}
          />
        </div>
      </div>

      <div className="flex justify-end px-4">
        <Button
          label={'Save'}
          type="submit"
          loading={isLoading}
          loadingLabel='Saving...'
          className={`bg-btnBg rounded-lg !py-1 text-white ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={isLoading || !!successMessage}
        />
      </div>
    </form>
  );
};

export default AddModal;